import * as React from 'react';
import {
    Button,
    Field,
    Input,
    Label,
    makeStyles,
    Select,
    SpinButton,
    Switch,
    Text,
    tokens,
} from '@fluentui/react-components';
import { Add20Regular, Dismiss20Regular, Library20Regular } from '@fluentui/react-icons';
import { IPollTemplate, PollQuestionType } from '../../services/DataService';
import { QUESTION_TYPE_OPTIONS } from '../../lib/pollLabels';
import {
    applyQuestionType,
    IEditableQuestion,
    isChoiceType,
    isTextType,
    questionFromTemplate,
    emptyOption,
    RATING_SCALE_MAX,
    RATING_SCALE_MIN,
} from '../../lib/pollQuestions';
import TemplatePickerDialog from './TemplatePickerDialog';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
        flexWrap: 'wrap',
    },
    options: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    optionRow: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        gap: '8px',
        alignItems: 'center',
    },
    range: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: '12px',
        maxWidth: '360px',
    },
    hint: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
    },
    requiredRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        paddingTop: '4px',
    },
});

export interface IQuestionEditorProps {
    question: IEditableQuestion;
    onChange: (question: IEditableQuestion) => void;
    /** Hide the type selector (the quick wizard picks the type on its own step). */
    hideType?: boolean;
    /** Hide the «required» switch (quick polls have a single required question). */
    hideRequired?: boolean;
    error?: string;
    /** Show validation for empty fields. */
    showErrors?: boolean;
    autoFocus?: boolean;
}

/** Edits one question: type, wording, options, range or placeholder. */
const QuestionEditor: React.FC<IQuestionEditorProps> = ({
    question, onChange, hideType, hideRequired, error, showErrors, autoFocus,
}) => {
    const styles = useStyles();
    const [pickerOpen, setPickerOpen] = React.useState(false);

    const patch = (values: Partial<IEditableQuestion>): void => onChange({ ...question, ...values });

    const setOption = (index: number, value: string): void => {
        const options = question.Options.slice();
        options[index] = { ...options[index], Text: value };
        patch({ Options: options });
    };

    const removeOption = (index: number): void => {
        const options = question.Options.filter((_, i) => i !== index);
        patch({ Options: options.length >= 2 ? options : [...options, emptyOption()] });
    };

    const applyTemplate = (template: IPollTemplate): void => {
        setPickerOpen(false);
        onChange(questionFromTemplate(template, question));
    };

    const titleError = showErrors && !question.Title.trim() ? 'Укажите текст вопроса.' : undefined;
    const filledOptions = question.Options.filter((o) => o.Text.trim()).length;
    const optionsError = showErrors && isChoiceType(question.Type) && filledOptions < 2
        ? 'Нужно минимум два заполненных варианта.'
        : undefined;
    const clampScale = (value: number): number => Math.min(RATING_SCALE_MAX, Math.max(RATING_SCALE_MIN, Math.trunc(value)));
    const rangeError = showErrors && question.Type === PollQuestionType.Rating
        ? (question.MinValue < RATING_SCALE_MIN || question.MaxValue > RATING_SCALE_MAX
            ? `Шкала должна быть в пределах от ${RATING_SCALE_MIN} до ${RATING_SCALE_MAX}.`
            : question.MinValue >= question.MaxValue ? 'Минимум должен быть меньше максимума.' : undefined)
        : undefined;

    return (
        <div className={styles.root}>
            <div className={styles.toolbar}>
                {!hideType ? (
                    <Field label="Тип вопроса" style={{ flexGrow: 1, maxWidth: 320 }}>
                        <Select
                            value={String(question.Type)}
                            onChange={(_, data) => onChange(applyQuestionType(question, Number(data.value) as PollQuestionType))}
                        >
                            {QUESTION_TYPE_OPTIONS.map((option) => (
                                <option key={option.value} value={String(option.value)}>{option.label}</option>
                            ))}
                        </Select>
                    </Field>
                ) : <span />}
                <Button appearance="subtle" icon={<Library20Regular />} onClick={() => setPickerOpen(true)}>
                    Из шаблона
                </Button>
            </div>

            <Field label="Текст вопроса" required validationMessage={titleError ?? (error && !titleError && !optionsError && !rangeError ? error : undefined)}>
                <Input
                    value={question.Title}
                    autoFocus={autoFocus}
                    maxLength={500}
                    placeholder="Например: Насколько полезной была лекция?"
                    onChange={(_, data) => patch({ Title: data.value })}
                />
            </Field>

            {isChoiceType(question.Type) && (
                <Field label="Варианты ответа" required validationMessage={optionsError}>
                    <div className={styles.options}>
                        {question.Options.map((option, index) => (
                            <div key={index} className={styles.optionRow}>
                                <Input
                                    value={option.Text}
                                    placeholder={`Вариант ${index + 1}`}
                                    aria-label={`Вариант ${index + 1}`}
                                    onChange={(_, data) => setOption(index, data.value)}
                                />
                                <Button
                                    appearance="subtle"
                                    icon={<Dismiss20Regular />}
                                    aria-label={`Удалить вариант ${index + 1}`}
                                    disabled={question.Options.length <= 2}
                                    onClick={() => removeOption(index)}
                                />
                            </div>
                        ))}
                        <div>
                            <Button
                                appearance="secondary"
                                size="small"
                                icon={<Add20Regular />}
                                onClick={() => patch({ Options: [...question.Options, emptyOption()] })}
                            >
                                Добавить вариант
                            </Button>
                        </div>
                    </div>
                </Field>
            )}

            {question.Type === PollQuestionType.Rating && (
                <div>
                    <Label>Диапазон оценок</Label>
                    <div className={styles.range} style={{ marginTop: 4 }}>
                        <Field label="Минимум" size="small" validationMessage={rangeError}>
                            <SpinButton
                                value={question.MinValue}
                                min={RATING_SCALE_MIN}
                                max={question.MaxValue - 1}
                                onChange={(_, data) => {
                                    const value = data.value ?? Number(data.displayValue);
                                    if (!Number.isNaN(value) && value !== null) patch({ MinValue: clampScale(Number(value)) });
                                }}
                            />
                        </Field>
                        <Field label="Максимум" size="small">
                            <SpinButton
                                value={question.MaxValue}
                                min={question.MinValue + 1}
                                max={RATING_SCALE_MAX}
                                onChange={(_, data) => {
                                    const value = data.value ?? Number(data.displayValue);
                                    if (!Number.isNaN(value) && value !== null) patch({ MaxValue: clampScale(Number(value)) });
                                }}
                            />
                        </Field>
                    </div>
                </div>
            )}

            {question.Type === PollQuestionType.Nps && (
                <Text className={styles.hint}>Для NPS используется фиксированная шкала от 0 до 10.</Text>
            )}

            {isTextType(question.Type) && (
                <Field label="Подсказка в поле ответа" hint="Показывается серым текстом, пока участник ничего не ввёл.">
                    <Input
                        value={question.Placeholder}
                        placeholder="Например: Напишите пару слов"
                        onChange={(_, data) => patch({ Placeholder: data.value })}
                    />
                </Field>
            )}

            {!hideRequired && (
                <div className={styles.requiredRow}>
                    <Text>Обязательный вопрос</Text>
                    <Switch
                        checked={question.Required}
                        aria-label="Обязательный вопрос"
                        onChange={(_, data) => patch({ Required: !!data.checked })}
                    />
                </div>
            )}

            <TemplatePickerDialog open={pickerOpen} onClose={() => setPickerOpen(false)} onPick={applyTemplate} />
        </div>
    );
};

export default QuestionEditor;
